import React, { memo, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { Modal, ModalBody, ModalFooter, ModalHeader } from 'reactstrap';
import { CancelToken, isCancel } from 'axios';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCircleNotch } from '@fortawesome/free-solid-svg-icons';
import PhoneInput from 'react-phone-input-2';
import 'react-phone-input-2/lib/bootstrap.css';

import SubscriptionService from '../../Services/SubscriptionService';

export default memo(NonPlatformUserSubscriptionModal);

const initState = {
  email: '',
  error: '',
  sms: '',
  subsRequestPending: false,
};

function NonPlatformUserSubscriptionModal({
  asset,
  handleNonPlatformSubAdded,
  isOpen,
  toggleModal,
}) {
  const [state, updateState] = useState(initState);

  const source = CancelToken.source();
  const subService = useMemo(() => new SubscriptionService(), []);

  const generateRequests = (Notification, Destination) => {
    const commonParams = {
      cancelToken: source.token,
      params: { orgID: asset.orgID },
    };
    const dm1RequestParams = {
        ...commonParams,
        data: {
          Destination,
          GatewayId: asset._id,
          Notification,
          RuleId: '*',
          Trigger: 'dm1',
        },
      },
      rulesRequestParams = {
        ...commonParams,
        data: {
          Destination,
          Notification,
          SourceId: asset._id,
          SourceType: 'gateway',
          Trigger: 'rule',
        },
      },
      maintenanceRequestParams = {
        ...commonParams,
        data: {
          Destination,
          Notification,
          SourceId: asset._id,
          SourceType: 'gateway',
          Trigger: 'maintenance',
        },
      };
    return [
      subService.postSubscription(dm1RequestParams),
      subService.subscribeAll(rulesRequestParams),
      subService.subscribeAll(maintenanceRequestParams),
    ];
  };

  const handlePropUpdate = ({ target: { name, value } }) => {
    updateState((prevState) => ({ ...prevState, [name]: value }));
  };

  const handleSmsUpdate = (phoneNumber) => {
    const sms = phoneNumber.replace(/\D/g, '');
    updateState((prevState) => ({ ...prevState, sms }));
  };

  const validateInput = (email, sms) => {
    let error = '';
    updateState((prevState) => ({ ...prevState, error }));
    const smsPattern = /^\d{11}$/;
    const emailPattern = /^[a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,4}$/;
    if (!email.length && !sms.length) {
      error = 'Email and sms fields are both empty.';
    }
    if (sms.length && !smsPattern.test(sms)) {
      error = 'Sms number is invalid. ';
    }
    if (email.length && !emailPattern.test(email)) {
      error += 'Email address is invalid.';
    }

    if (error) {
      throw new Error(error);
    }
  };

  const handleSubSave = async () => {
    try {
      const { email, sms } = state;

      validateInput(email, sms);
      updateState((prevState) => ({ ...prevState, subsRequestPending: true }));

      const { destinations, requests } = [email, sms].reduce(
        (acc, destination, i) => {
          if (!destination.length) return acc;

          switch (i) {
            case 0: {
              acc.destinations.push(destination);
              const emailRequests = generateRequests('email', destination);
              acc.requests.push(...emailRequests);
              break;
            }
            case 1: {
              acc.destinations.push(`+${destination}`);
              const smsRequests = generateRequests('sms', `+${destination}`);
              acc.requests.push(...smsRequests);
              break;
            }
          }
          return acc;
        },
        { destinations: [], requests: [] }
      );
      await Promise.all(requests);
      window.setTimeout(() => handleNonPlatformSubAdded(destinations), 5000);
    } catch (err) {
      if (isCancel(err)) return;
      const error = err.response?.data?.message ?? err.message;
      updateState((prevState) => ({
        ...prevState,
        error,
        subsRequestPending: false,
      }));
    }
  };

  useEffect(() => source.cancel, []);

  return (
    <Modal
      backdrop={state.subsRequestPending ? 'static' : true}
      centered
      isOpen={isOpen}
      scrollable={false}
      toggle={!state.subsRequestPending ? toggleModal : () => null}
    >
      <ModalHeader
        className="border-bottom-0"
        toggle={!state.subsRequestPending ? toggleModal : () => null}
      >
        Subscribe <span className="d-none d-md-inline">Non-Platform </span>User
      </ModalHeader>
      <ModalBody className="container-fluid pt-0">
        <div className="row">
          <p className="col-12 modal-supplemental-text">
            When notifications need to go to emails or sms for users without an
            account you can add them here and subscribe to DM1 Errors and Rules
          </p>
        </div>
        <div className="row">
          <div className="form-group col-12 col-md-8">
            <label className="modal-inputlabel-text" htmlFor="email">
              Email
            </label>
            <input
              className="form-control rounded"
              name="email"
              onChange={handlePropUpdate}
              placeholder="some@email.com"
              readOnly={state.subsRequestPending}
              type="text"
              value={state.email}
            />
          </div>
        </div>
        <div className="row">
          <div className="form-group col-12 col-md-8">
            <label className="modal-inputlabel-text" htmlFor="sms">
              Phone Number
            </label>
            <PhoneInput
              country="us"
              enableSearch
              inputClass="w-100 py-0 rounded modal-placeholder-text"
              name="sms"
              onChange={handleSmsUpdate}
              readOnly={state.subsRequestPending}
              value={state.sms}
            />
          </div>
        </div>
        <div className="row justify-content-end">
          <p
            className={
              state.error
                ? 'col-12 text-right text-danger three-quarter-text mb-0'
                : 'd-none'
            }
          >
            {state.error}
          </p>
        </div>
        <div className="row justify-content-end">
          <p
            className={
              state.subsRequestPending
                ? 'col-12 text-right modal-supplemental-text mb-0'
                : 'd-none'
            }
          >
            This request may take several seconds to complete. Please do not
            refresh your browser.
          </p>
        </div>
      </ModalBody>
      <ModalFooter className="border-top-0">
        <button
          className="btn btn-outline-secondary text-muted bg-white shadow-none py-0 px-3 three-quarters-text"
          disabled={state.subsRequestPending}
          onClick={toggleModal}
        >
          Cancel
        </button>
        <button
          className="btn modal-button-submit py-0 px-5 three-quarters-text"
          disabled={state.subsRequestPending}
          onClick={handleSubSave}
        >
          {state.subsRequestPending ? (
            <FontAwesomeIcon icon={faCircleNotch} spin />
          ) : (
            'Add'
          )}
        </button>
      </ModalFooter>
      <style jsx>{`
        .btn:focus {
          outline: none;
        }
      `}</style>
    </Modal>
  );
}

NonPlatformUserSubscriptionModal.propTypes = {
  asset: PropTypes.shape({
    _id: PropTypes.string,
    orgID: PropTypes.string,
  }),
  handleNonPlatformSubAdded: PropTypes.func,
  isMaintenance: PropTypes.bool,
  isOpen: PropTypes.bool,
  toggleModal: PropTypes.func,
};
